import { Button } from "@helmr/ui";
import { useQuery } from "@tanstack/react-query";
import { listStaffNames } from "../../api/audit";
import type { DayHours, Hours } from "../../api/settings";
import { CsvInput, useScope } from "./kit";
import type { CustomProps } from "./SectionForm";
import s from "./settings.module.css";

export function useStaffNames() {
  const sc = useScope();
  return useQuery({ queryKey: sc.key("staff-names"), queryFn: () => listStaffNames(sc.client), retry: false });
}

/** Staff as checkboxes. Needs `staff:view`; without it the ids are typed (the API still checks them). */
export function StaffPick({ value, onChange, disabled, id }: CustomProps) {
  const q = useStaffNames();
  const chosen = (value as string[] | null) ?? [];
  if (q.isPending) return <span className={s.hint}>Loading staff…</span>;
  if (q.error) return <CsvInput id={id} disabled={disabled} value={chosen} aria-label="Staff ids" onChange={onChange} placeholder="stf_… , stf_…" />;
  return (
    <div className={s.checks} role="group" aria-label="Staff">
      {q.data.map((p) => (
        <label key={p.id} className={s.check}>
          <input type="checkbox" disabled={disabled} checked={chosen.includes(p.id)} onChange={(e) => onChange(e.target.checked ? [...chosen, p.id] : chosen.filter((x) => x !== p.id))} />{p.name}
        </label>
      ))}
    </div>
  );
}

interface Rule { id: string; match: { sources?: string[]; branchIds?: string[] }; owners: string[] }

export function RulesEditor({ value, onChange, disabled, branches }: CustomProps & { branches: { id: string; name: string }[] }) {
  const rules = (value as Rule[] | null) ?? [];
  const staff = useStaffNames();
  const patch = (i: number, r: Rule) => onChange(rules.map((x, j) => (j === i ? r : x)));
  const toggle = (list: string[] | undefined, v: string, on: boolean) => (on ? [...(list ?? []), v] : (list ?? []).filter((x) => x !== v));
  // "Any" is the key being absent. To the lead rules an empty list matches nothing, so the rule would never fire.
  const withMatch = (r: Rule, key: keyof Rule["match"], list: string[]): Rule => {
    const { [key]: _drop, ...match } = r.match;
    return { ...r, match: list.length ? { ...match, [key]: list } : match };
  };
  return (
    <div className={s.stack}>
      {rules.length === 0 ? <span className={s.hint}>No rules: every lead goes to the default owners.</span> : null}
      {rules.map((r, i) => (
        <fieldset key={i} className={s.stack} style={{ border: "1px solid var(--color-border-default)", borderRadius: "var(--radius-control)", padding: 12 }} disabled={disabled}>
          <legend className={s.hint}>Rule {i + 1}</legend>
          <label className={s.field}><span className={s.lab}>Rule name</span>
            <input className={s.input} value={r.id} onChange={(e) => patch(i, { ...r, id: e.target.value })} aria-label={`Rule ${i + 1} name`} /></label>
          <label className={s.field}><span className={s.lab}>Sources (comma separated, empty = any)</span>
            <CsvInput value={r.match.sources ?? []} onChange={(sources) => patch(i, withMatch(r, "sources", sources))} aria-label={`Rule ${i + 1} sources`} /></label>
          <div className={s.field}><span className={s.lab}>Branches (none ticked = any)</span>
            <div className={s.checks}>{branches.map((b) => (
              <label key={b.id} className={s.check}><input type="checkbox" checked={(r.match.branchIds ?? []).includes(b.id)} onChange={(e) => patch(i, withMatch(r, "branchIds", toggle(r.match.branchIds, b.id, e.target.checked)))} />{b.name}</label>
            ))}</div></div>
          <div className={s.field}><span className={s.lab}>Owners</span>
            <div className={s.checks}>{(staff.data ?? []).map((p) => (
              <label key={p.id} className={s.check}><input type="checkbox" checked={r.owners.includes(p.id)} onChange={(e) => patch(i, { ...r, owners: toggle(r.owners, p.id, e.target.checked) })} />{p.name}</label>
            ))}</div></div>
          <div><Button size="sm" onClick={() => onChange(rules.filter((_, j) => j !== i))}>Remove rule</Button></div>
        </fieldset>
      ))}
      {disabled ? null : <div><Button size="sm" onClick={() => onChange([...rules, { id: `rule_${rules.length + 1}`, match: {}, owners: [] }])}>Add rule</Button></div>}
    </div>
  );
}

const DAYS_SUN = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
type Day = { open: string; close: string } | null;

/** Seven days, Sunday first, one open/close pair or closed (the leads setting). Absent = the engagement package's default hours. */
export function WeekHoursEditor({ value, onChange, disabled }: CustomProps) {
  const week = (value as Day[] | null) ?? null;
  if (!week) return (
    <div className={s.stack}><span className={s.hint}>Using the standard hours.</span>
      {disabled ? null : <div><Button size="sm" onClick={() => onChange(DAYS_SUN.map((_, i) => (i === 0 ? null : { open: "06:00", close: "22:00" })))}>Set your own hours</Button></div>}</div>
  );
  const set = (i: number, d: Day) => onChange(week.map((x, j) => (j === i ? d : x)));
  return (
    <div className={s.stack}>
      {week.map((d, i) => (
        <div key={i} className={s.pair} style={{ gridTemplateColumns: "100px auto 1fr 1fr" }}>
          <span>{DAYS_SUN[i]}</span>
          <label className={s.check}><input type="checkbox" disabled={disabled} checked={d === null} onChange={(e) => set(i, e.target.checked ? null : { open: "06:00", close: "22:00" })} />Closed</label>
          <input type="time" className={s.input} disabled={disabled || d === null} value={d?.open ?? ""} aria-label={`${DAYS_SUN[i]} opens`} onChange={(e) => d && set(i, { ...d, open: e.target.value })} />
          <input type="time" className={s.input} disabled={disabled || d === null} value={d?.close ?? ""} aria-label={`${DAYS_SUN[i]} closes`} onChange={(e) => d && set(i, { ...d, close: e.target.value })} />
        </div>
      ))}
    </div>
  );
}

const WEEKDAYS = [["mon", "Monday"], ["tue", "Tuesday"], ["wed", "Wednesday"], ["thu", "Thursday"], ["fri", "Friday"], ["sat", "Saturday"], ["sun", "Sunday"]] as const;

/** A branch's own hours: one open/close interval per weekday (the API takes up to four; a split day is edited as a list of "06:00-10:00, 16:00-22:00"). */
export function BranchHoursEditor({ value, onChange, disabled }: CustomProps) {
  const hours = (value as Hours | null) ?? {};
  const text = (d: DayHours | undefined) => (d ?? []).map((iv) => `${iv.open}-${iv.close}`).join(", ");
  const parse = (raw: string): DayHours => raw.split(",").map((x) => x.trim()).filter(Boolean).map((x) => { const [open = "", close = ""] = x.split("-").map((p) => p.trim()); return { open, close }; });
  return (
    <div className={s.stack}>
      {WEEKDAYS.map(([k, label]) => (
        <div key={k} className={s.pair} style={{ gridTemplateColumns: "100px 1fr" }}>
          <span>{label}</span>
          <input className={s.input} disabled={disabled} defaultValue={text(hours[k])} key={`${k}:${text(hours[k])}`} placeholder="Closed" aria-label={`${label} hours`}
            onBlur={(e) => { const next = parse(e.target.value); const copy = { ...hours }; if (next.length) copy[k] = next; else delete copy[k]; onChange(Object.keys(copy).length ? copy : null); }} />
        </div>
      ))}
      <span className={s.hint}>Times as HH:MM-HH:MM, for example 06:00-22:00. Empty means closed. Leave every day empty to inherit.</span>
    </div>
  );
}
